import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Code, 
  Key, 
  Webhook, 
  TestTube,
  Book,
  Zap,
  Copy,
  Eye,
  Plus
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { SupabaseService } from '../lib/supabase'
import toast from 'react-hot-toast'

const DeveloperDashboard = () => {
  const { profile } = useAuth()
  const [apiKeys, setApiKeys] = useState([])
  const [webhooks, setWebhooks] = useState([])
  const [stats, setStats] = useState({
    apiCalls: 0,
    successRate: 0,
    lastCall: null
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDeveloperData()
  }, [])

  const loadDeveloperData = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      
      // Charger les vraies données depuis Supabase
      const [apiKeysData, webhooksData, paymentsData] = await Promise.all([
        SupabaseService.getApiKeys(user.id),
        SupabaseService.getWebhooks(user.id),
        SupabaseService.getPayments(user.id, 100)
      ])
      
      setApiKeys(apiKeysData)
      setWebhooks(webhooksData)
      
      // Calculer les statistiques réelles
      const apiCalls = paymentsData.length
      const completedPayments = paymentsData.filter(p => p.status === 'completed')
      const successRate = paymentsData.length > 0 ? (completedPayments.length / paymentsData.length) * 100 : 0
      const lastCall = paymentsData.length > 0 ? paymentsData[0].created_at : null
      
      setStats({
        apiCalls,
        successRate,
        lastCall
      })
      
    } catch (error) {
      console.error('Erreur chargement données développeur:', error)
      toast.error('Erreur lors du chargement')
      
      // Initialiser avec des valeurs vides
      setApiKeys([])
      setWebhooks([])
      setStats({
        apiCalls: 0,
        successRate: 0,
        lastCall: null
      })
    } finally {
      setLoading(false)
    }
  }

  const quickStartSteps = [
    {
      title: '1. Obtenir une clé API',
      description: 'Générez votre première clé API pour commencer',
      action: 'Créer une clé',
      icon: Key
    },
    {
      title: '2. Tester l\'API',
      description: 'Utilisez notre playground pour tester vos appels',
      action: 'Ouvrir Playground',
      icon: TestTube
    },
    {
      title: '3. Configurer les webhooks',
      description: 'Recevez des notifications en temps réel',
      action: 'Configurer',
      icon: Webhook
    },
    {
      title: '4. Intégrer le SDK',
      description: 'Utilisez notre SDK JavaScript/TypeScript',
      action: 'Voir la doc',
      icon: Code
    }
  ]

  const codeExamples = {
    javascript: `// Installation
npm install @payfuse/sdk

// Utilisation
import PayFuse from '@payfuse/sdk'

const payfuse = new PayFuse({
  apiKey: 'your_api_key',
  environment: 'sandbox'
})

const payment = await payfuse.payments.create({
  amount: 10000,
  currency: 'XOF',
  provider: 'orange_money',
  phone: '+221771234567'
})`,
    
    curl: `curl -X POST https://api.payfuse.dev/v1/payments \\
  -H "Authorization: Bearer your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 10000,
    "currency": "XOF",
    "provider": "orange_money",
    "phone": "+221771234567"
  }'`
  }

  const [selectedExample, setSelectedExample] = useState('javascript')

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Espace Développeur
        </h1>
        <p className="text-gray-600">
          Bienvenue {profile?.name}, intégrez PayFuse dans vos applications
        </p>
      </motion.div>

      {/* Stats rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex items-center">
            <Zap className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.apiCalls}</p>
              <p className="text-sm text-gray-600">Appels API ce mois</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex items-center">
            <TestTube className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.successRate}%</p>
              <p className="text-sm text-gray-600">Taux de succès</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex items-center">
            <Key className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <p className="text-2xl font-bold text-gray-900">{apiKeys.length}</p>
              <p className="text-sm text-gray-600">Clés API actives</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Guide de démarrage rapide */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Book className="w-5 h-5 mr-2 text-blue-600" />
            Démarrage Rapide
          </h2>
          
          <div className="space-y-4">
            {quickStartSteps.map((step, index) => (
              <div key={index} className="flex items-start p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                <step.icon className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{step.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                </div>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  {step.action}
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Exemples de code */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Code className="w-5 h-5 mr-2 text-green-600" />
            Exemples de Code
          </h2>
          
          <div className="flex space-x-2 mb-4">
            {Object.keys(codeExamples).map((lang) => (
              <button
                key={lang}
                onClick={() => setSelectedExample(lang)}
                className={`px-3 py-1 text-sm rounded ${
                  selectedExample === lang
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {lang === 'javascript' ? 'JavaScript' : 'cURL'}
              </button>
            ))}
          </div>
          
          <div className="relative">
            <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{codeExamples[selectedExample as keyof typeof codeExamples]}</code>
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(codeExamples[selectedExample as keyof typeof codeExamples])
                toast.success('Code copié!')
              }}
              className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Clés API */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm border p-6 mb-8"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold flex items-center">
            <Key className="w-5 h-5 mr-2 text-purple-600" />
            Mes Clés API
          </h2>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Clé
          </button>
        </div>
        
        <div className="space-y-3">
          {apiKeys.map((key: any) => (
            <div key={key.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium">{key.name}</h3>
                <p className="text-sm text-gray-600 font-mono">{key.key_preview}</p>
                <div className="flex items-center mt-2 space-x-4">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {key.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    Dernière utilisation: {new Date(key.last_used).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button className="p-2 text-gray-600 hover:text-gray-900">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="p-2 text-gray-600 hover:text-gray-900">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Ressources utiles */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <Book className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Documentation</h3>
          <p className="text-sm text-gray-600 mb-4">Guide complet d'intégration</p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Consulter
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <TestTube className="w-8 h-8 text-green-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">API Playground</h3>
          <p className="text-sm text-gray-600 mb-4">Testez vos intégrations</p>
          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            Tester
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
          <Webhook className="w-8 h-8 text-purple-600 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Webhooks</h3>
          <p className="text-sm text-gray-600 mb-4">Notifications temps réel</p>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
            Configurer
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default DeveloperDashboard